import React, { FC } from "react";
import classnames from "classnames";
import { Button } from "@shared/components";

// Relative, not the "@shared/components" alias: jest resolves no path aliases, and this component's
// own test (RankRow.test.tsx) needs InitialsAvatar's REAL rendered initials, not a stubbed marker.
// `getInitials` (the pure derivation, not the component) is reused by the not-ranked variant below,
// which needs the SAME two-letter initials but cannot use InitialsAvatar itself -- that shared
// component's own `size` union (26|40|52) and its SCSS's fixed $secondary/white treatment have no 32px
// / #ede9fa-on-$main-color2 member, and broadening a SHARED component for this one row is out of scope
// here (Figma `Leaderboard`, 9289:2435 -- "you . not ranked").
import { AvatarImage, InitialsAvatar, getInitials } from "../../../../../shared/components/InitialsAvatar";
import { railFillPercent } from "./railFillPercent";
// L5 (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md): the per-row value chip's letter badge and short label --
// both live on this Leaderboard shelf (crownBadgeLetter was hoisted here for exactly this second
// consumer; see its own JSDoc).
import { crownBadgeLetter } from "../crownBadgeLetter";
import { leaderboardValueChipLabel } from "../leaderboardValueChipLabel";
// Relative, not the "@assets" alias, for the same reason as the InitialsAvatar import above: jest
// resolves no path aliases. This is the real hexagon asset exported from the frame (Figma
// `Leaderboard`, 9119:1089), replacing the hand-computed `clip-path: polygon(...)` this badge used to
// draw itself with.
import { ReactComponent as ValueBadgeHexIcon } from "../../../../../assets/images/icons/spark/value-badge-hex.svg";
import "./index.scss";

interface RankRowRankedProps {
  // Explicit, never derived from an array index internally. The inline rows on both screens ARE
  // index-derived, but by their own caller (`OverviewLeaderboard.tsx` and `LeaderboardsContainer.tsx`
  // each pass `index + 4`, continuing below their own three podium tiles) -- whereas EITHER screen's
  // pinned "you" row takes its rank from the api's own `viewer.rank` and can sit well outside the
  // fetched window. Taking `rank` as a plain prop, rather than an index, is what lets every call site
  // use the same component correctly.
  rank: number;
  firstName: string;
  lastName: string;
  /** google-profile-pictures-plan.md Phase 2 step 3: the person's photo URL, if any -- threaded from
   *  the caller's own leaderboard row (`LeaderboardUser.avatar_url`). Falls back to the initials, same
   *  as `InitialsAvatar`'s own `imageUrl` prop. */
  imageUrl?: string | null;
  totalScore: number;
  // The board's #1 score for the same board/period -- `SPARK/Rail`'s own fill rule reads THIS,
  // never the row immediately above (see railFillPercent's own comment).
  leaderScore: number;
  // "This row is the logged-in viewer." ONE prop, deliberately, for the whole treatment: it both
  // highlights the row and swaps the shown name to "You". Those were two independent optional props
  // (`highlighted` + `displayName`) that every call site had to remember to move together to express a
  // single rule -- setting one without the other compiled fine and produced a half-marked row. Same
  // prop name and same convention as `PodiumTile`'s own `isViewer`, so the podium and the rows say
  // "that's you" the one way. The avatar always gets the real firstName/lastName, so the initials stay
  // the person's own.
  isViewer?: boolean;
  /**
   * L5 (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md): the person's top-scoring core value's title for this
   * board/period window (`LeaderboardEntry.top_core_value_id`, resolved to a title by the caller --
   * this component takes only primitives, matching every other prop here). `null`/`undefined` render
   * NO chip, never a placeholder: the api's `top_core_value_id: null` is a real "nothing to show" state
   * (LeaderboardEntry's own model comment), not missing data. Never supplied for the pinned "you" row
   * (`LeaderboardViewer` carries no such field at all) -- per the design, the chip only ever appears on
   * ranked rows 4+.
   */
  topCoreValueTitle?: string | null;
  /**
   * L9 (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md): opens the "New Recognition" form prefilled with this row's
   * person. The CALLER (`LeaderboardsContainer`) owns the `showOverlay` dispatch and binds this to its
   * own row -- this component stays presentational, matching how `SparkSpreadSection`/
   * `MySparksContainer` already split a card from its container's dispatch. Renders the button ONLY
   * when supplied AND `isViewer` is not true: self-recognition is rejected server-side
   * (`assertSelfRecognition`), so the pinned "you" row must never carry a button that guarantees a
   * 400 -- enforced HERE, not only by the caller omitting the prop, so a caller mistake cannot reach it
   * either.
   */
  onRecognise?: () => void;
}

/**
 * The pinned "you" row's OTHER shape: the viewer has no score at all on this board/period
 * (`LeaderboardViewer` itself is `null` -- `computeViewerRank`'s own contract, "never a rank of 0").
 * `rank: null` is the discriminant `RankRowProps` branches on, rather than a second boolean, so a
 * caller cannot supply this shape's fields (`caption`) alongside a real `rank` by mistake. Always the
 * viewer's own row -- there is no ranked/not-ranked choice for anyone else -- so unlike
 * `RankRowRankedProps` there is no `isViewer` here: the name is unconditionally "You".
 */
interface RankRowUnrankedProps {
  rank: null;
  firstName: string;
  lastName: string;
  /** Same as `RankRowRankedProps`' own `imageUrl` -- see that prop's own comment. */
  imageUrl?: string | null;
  /**
   * The caption under the rail (Figma `Leaderboard`, 9289:2435 -- "you . not ranked"), e.g.
   * "Not ranked this quarter.". Supplied by the CALLER, already resolved for the selected period
   * (`leaderboardViewerUnrankedShortCopy`) -- this component takes only primitives, matching every
   * other prop on this shelf, and has no notion of `LEADERBOARD_PERIOD` of its own.
   */
  caption: string;
}

type RankRowProps = RankRowRankedProps | RankRowUnrankedProps;

/**
 * The row action's label. Shared by the real button and the not-ranked row's hidden footprint copy, so
 * the two can never render different text and therefore different widths -- which is the whole point of
 * the spacer.
 */
const RECOGNISE_LABEL = "Recognise";

/** The name shown in place of a viewer's own -- `PodiumTile` renders the same word for the same rule. */
const VIEWER_DISPLAY_NAME = "You";

/**
 * One ranked row below the podium (Figma `Leaderboard`, 9119:1089) -- rank numeral, a 26px
 * `InitialsAvatar`, the person's name over a `SPARK/Rail` distance-from-leader bar, then their total.
 * Both screens use it the same two ways: for the inline ranks below their podium, and for their own
 * pinned "you" row. Which one a given instance is depends only on the props its caller passes in, not
 * on anything this component derives itself.
 *
 * Moved out of `components/MySparks/OverviewLeaderboard/RankRow` to `components/Leaderboard/RankRow`
 * (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md's own L1) once the Leaderboard tab became a second consumer. It
 * sits in the Leaderboard folder, NOT in `shared/components`: it takes only primitives, but a ranked
 * row is a leaderboard concept, and both consumers live under `containers/Recognition/` -- the same
 * shelf `LeaderboardBoardToggle`, `LeaderboardLoadStatus` and `leaderboardEmptyCopy` are already
 * shared from, MySparks reaching into it exactly as it does for those three.
 *
 * A second shape (`RankRowUnrankedProps`, `rank: null`) renders the pinned "you" row's OTHER state --
 * the viewer has no score at all on this board/period (Figma `Leaderboard`, 9289:2435 -- "you . not
 * ranked"): em dashes in place of the rank and score, a bare rail track with no fill, and a caption
 * under the name instead of the value chip/Recognise slot. `LeaderboardsContainer` is this variant's
 * only caller today (`OverviewLeaderboard`'s own not-ranked state stays the short text line it already
 * had -- out of scope here).
 *
 * @param props.rank - the row's ordinal rank (1-based), rendered as-is, or `null` for the not-ranked
 *   variant (see `RankRowUnrankedProps`).
 * @param props.firstName - the person's first name.
 * @param props.lastName - the person's last name.
 * @param props.imageUrl - the person's photo URL, if any (see this prop's own interface comment).
 * @param props.totalScore - the person's total score for the board/period this row renders.
 * @param props.leaderScore - the board's #1 score, used to size the rail (railFillPercent).
 * @param props.isViewer - true when this row is the logged-in viewer's own: highlights it AND shows
 *   "You" in place of their name. The avatar still uses firstName/lastName, so the initials stay the
 *   person's own.
 * @param props.topCoreValueTitle - see the prop's own interface comment.
 * @param props.onRecognise - see the prop's own interface comment.
 */
const RankRow: FC<RankRowProps> = (props) => {
  // The discriminant: `rank === null` is the ENTIRE not-ranked branch, checked once here rather than
  // scattered through the JSX below (every other field below narrows off this one check, TS-verified --
  // `props.caption` is only reachable inside this branch, `props.totalScore` only outside it).
  if (props.rank === null) {
    const { firstName, lastName, imageUrl, caption } = props;
    return (
      <div className="rank-row rank-row--unranked" data-testid="rank-row-unranked">
        {/* Rank slot: an em dash, never "0" -- there is no rank to show, and "0" would read as one. */}
        <div className="rank-row-rank rank-row-rank--unranked">—</div>
        {/* Not `InitialsAvatar`: its `size` union (26|40|52) has no 32px member and its SCSS hardcodes
            $secondary/white, neither of which this row's #ede9fa/$main-color2 treatment can express
            without widening that SHARED component for one caller -- see this file's own import comment.
            `AvatarImage` (the same image-with-fallback InitialsAvatar itself uses) keeps the photo/
            initials logic un-duplicated; `getInitials` builds its fallback content. */}
        <div className="rank-row-unranked-avatar" data-testid="initials-avatar">
          <AvatarImage imageUrl={imageUrl} fallback={getInitials(firstName, lastName)} />
        </div>
        <div className="rank-row-unranked-main">
          <div className="rank-row-unranked-name">{VIEWER_DISPLAY_NAME}</div>
          {/* The rail TRACK only -- deliberately no fill child at all (not a fill pinned to 0%): there
              is no score to size a fill from, and a 0%-wide fill is still a fill element, which the
              frame does not have. */}
          <div className="rank-row-unranked-rail" />
          <div className="rank-row-unranked-caption">{caption}</div>
        </div>
        {/* No top-value chip renders here (hidden in the frame) -- but its BOX still needs to, so
            `.rank-row-unranked-main`'s `flex: 1` shrinks to the same width it has on a ranked row that
            does have one, and the rail above therefore ends at the same x as the ranked rows' own
            rails (pavel, 2026-09-22: "you don't match the row above"). `visibility: hidden`, not
            omitted -- see this class's own index.scss comment for why the chip's own box is reused
            rather than a hardcoded rail width. Also no Recognise button (this is always the viewer's
            own row -- self-recognition is rejected server-side, same rule the ranked branch's
            `isViewer` enforces below). */}
        <div className="rank-row-value-chip rank-row-value-chip--spacer" aria-hidden="true" />
        <div className="rank-row-total rank-row-total--unranked">—</div>
        {/* The Recognise button's footprint, reserved the same way the chip's is above. A pinned row
            never carries the button (this is always the viewer's own row, and self-recognition is
            rejected server-side), but every INLINE row does -- so without this the not-ranked rail runs
            ~120px past where the rails above it stop, which is what pavel marked.
            This is where the frame's otherwise-inexplicable 412px rail comes from: an inline row's own
            `who` column is exactly 412 wide once the chip (148) and the button (104) and their gaps are
            taken out of the 840 row, so the designer sized the not-ranked rail to match the INLINE rows
            rather than the pinned row's wider container.
            The real `Button` is rendered rather than a bare div: its box comes from the shared Button's
            own rules as much as from `.rank-row-recognise-button`, and only the real element is
            guaranteed to reserve the identical width. `visibility: hidden` also takes it out of the tab
            order, so there is no phantom focus stop. */}
        <Button
          type="button"
          className="rank-row-recognise-button rank-row-recognise-button--spacer"
          aria-hidden="true"
          tabIndex={-1}
        >
          {RECOGNISE_LABEL}
        </Button>
      </div>
    );
  }

  const { rank, firstName, lastName, imageUrl, totalScore, leaderScore, isViewer, topCoreValueTitle, onRecognise } =
    props;
  const fillPercent = railFillPercent(totalScore, leaderScore);
  const fullName = `${firstName} ${lastName}`;
  // L9: see this prop's own interface comment for why `!isViewer` is enforced here, not only by the
  // caller never passing `onRecognise` to the pinned row.
  const showRecogniseButton = Boolean(onRecognise) && !isViewer;
  // The viewer's own INLINE row carries no Recognise button (above), but every other row in the same
  // list does -- so without holding its footprint the viewer's chip and total slide right and break the
  // column alignment down the whole board. pavel, 2026-09-22, on the row ranked 8th: "Do you see the
  // problem?" -- the chip and score on that row sat ~140px right of every other row's.
  //
  // Gated on `onRecognise` being supplied, NOT just on `isViewer`: when a caller passes no handler at
  // all (the Overview rail renders these rows without any button), NO row has a button, so reserving
  // space would add a phantom gap to every row instead of removing one.
  const reserveRecogniseFootprint = Boolean(onRecognise) && isViewer;

  return (
    <div className={classnames("rank-row", { "rank-row--highlighted": isViewer })} data-testid={`rank-row-${rank}`}>
      <div className="rank-row-rank">{rank}</div>
      <InitialsAvatar firstName={firstName} lastName={lastName} size={26} imageUrl={imageUrl} />
      <div className="rank-row-main">
        <div className="rank-row-name">{isViewer ? VIEWER_DISPLAY_NAME : fullName}</div>
        <div className="rank-row-rail">
          <div className="rank-row-rail-fill" style={{ width: `${fillPercent}%` }} />
        </div>
      </div>
      {/* L5: between the name rail and the score, per the frame. No placeholder chip for a null/absent
          title -- see this prop's own interface comment. */}
      {topCoreValueTitle ? (
        <div className="rank-row-value-chip" data-testid={`rank-row-value-chip-${rank}`}>
          <span className="rank-row-value-chip-badge">
            <ValueBadgeHexIcon className="rank-row-value-chip-badge-icon" aria-hidden="true" focusable="false" />
            <span className="rank-row-value-chip-badge-letter">{crownBadgeLetter(topCoreValueTitle)}</span>
          </span>
          <span className="rank-row-value-chip-label">{leaderboardValueChipLabel(topCoreValueTitle)}</span>
        </div>
      ) : null}
      <div className="rank-row-total">{totalScore}</div>
      {showRecogniseButton ? (
        // Deliberately NOT the solid `.accent-solid` fill the "Spread your Sparks" CARD button uses
        // (`CollaboratorCard`, and the Redeem card it now shares that modifier with): seven solid
        // buttons stacked down this list would shout over the board they sit in. This row action is the
        // frame's own ghost treatment instead -- white, #ede9fa hairline, $secondary label
        // (`.rank-row-recognise-button`, index.scss) -- so it reads as secondary to the card action.
        // Do not "unify" the two; the weight difference is the point, not an inconsistency.
        <Button
          type="button"
          className="rank-row-recognise-button"
          data-testid={`rank-row-recognise-${rank}`}
          aria-label={`Recognise ${fullName}`}
          onClick={onRecognise}
        >
          {RECOGNISE_LABEL}
        </Button>
      ) : null}
      {reserveRecogniseFootprint ? (
        // Same footprint-holding device the not-ranked row uses, and the same reasoning: the REAL
        // Button, hidden, because its width comes from the shared Button's own rules as much as from
        // `.rank-row-recognise-button`, and only the real element reserves the identical box.
        // `visibility: hidden` keeps the box and drops it from the tab order.
        <Button
          type="button"
          className="rank-row-recognise-button rank-row-recognise-button--spacer"
          aria-hidden="true"
          tabIndex={-1}
        >
          {RECOGNISE_LABEL}
        </Button>
      ) : null}
    </div>
  );
};

export default RankRow;
